import { usePageHeader } from "contexts/PageHeaderContext";
import { useEffect, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectValue } from '../../ui/select';
import {
  GitBranch,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../ui/utils';

interface WorkflowMasterPageProps {
  onNavigate: (page: string) => void;
}

interface ApprovalLevel {
  id: string;
  levelNumber: number;
  name: string;
  type: string;
  employee: string;
  department: string;
  designation: string;
  fallbackApprover: string;
  canApprove: boolean;
  canReject: boolean;
  mandatoryLevel: boolean;
  image?: string;
}

interface WorkflowConfig {
  id: string;
  module: string;
  workflowLabel: string;
  validFrom: string;
  validTo: string;
  slaHours: string;
  escalationAfterHours: string;
  active: boolean;
  autoApprove: boolean;
  notifyApplicant: boolean;
  notifyOnReject: boolean;
  levels: ApprovalLevel[];
}

export function WorkflowMasterPage({ onNavigate }: WorkflowMasterPageProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Workflow Master",
      icon: <GitBranch className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [workflows, setWorkflows] = useState<WorkflowConfig[]>([
    {
      id: '1',
      module: 'Leave Approval',
      workflowLabel: 'Leave Approval',
      validFrom: '2025-01-01',
      validTo: '2025-12-31',
      slaHours: '24',
      escalationAfterHours: '48',
      active: true,
      autoApprove: false,
      notifyApplicant: true,
      notifyOnReject: true,
      levels: [
        {
          id: '1-1',
          levelNumber: 1,
          name: 'Direct Supervisor',
          type: 'By Designation',
          employee: '',
          department: 'Any',
          designation: 'Supervisor',
          fallbackApprover: 'Sheikh Mohammed Hussein',
          canApprove: true,
          canReject: true,
          mandatoryLevel: true
        },
        {
          id: '1-2',
          levelNumber: 2,
          name: 'Department Head',
          type: 'Specific Employee',
          employee: 'Husain Saifuddin Khambi',
          department: '',
          designation: '',
          fallbackApprover: 'Taher Husain Fakhri',
          canApprove: true,
          canReject: true,
          mandatoryLevel: false
        }
      ]
    },
    {
      id: '2',
      module: 'Advance Receipt Approval',
      workflowLabel: 'Advance Receipt Approval',
      validFrom: '2025-01-01',
      validTo: '2025-12-31',
      slaHours: '24',
      escalationAfterHours: '48',
      active: true,
      autoApprove: false,
      notifyApplicant: true,
      notifyOnReject: false,
      levels: [
        {
          id: '2-1',
          levelNumber: 1,
          name: 'Direct Supervisor',
          type: 'By Designation',
          employee: '',
          department: 'Finance',
          designation: 'Manager',
          fallbackApprover: 'None',
          canApprove: true,
          canReject: true,
          mandatoryLevel: true
        }
      ]
    },
    {
      id: '3',
      module: 'Payroll Processing Approval',
      workflowLabel: 'Payroll Processing Approval',
      validFrom: '2025-01-01',
      validTo: '2025-12-31',
      slaHours: '24',
      escalationAfterHours: '48',
      active: false,
      autoApprove: false,
      notifyApplicant: false,
      notifyOnReject: false,
      levels: []
    }
  ]);

  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowConfig | null>(workflows[0]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<WorkflowConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingWorkflow, setDeletingWorkflow] = useState<WorkflowConfig | null>(null);

  const [formData, setFormData] = useState<WorkflowConfig>({
    id: '',
    module: '',
    workflowLabel: '',
    validFrom: '',
    validTo: '',
    slaHours: '24',
    escalationAfterHours: '48',
    active: true,
    autoApprove: false,
    notifyApplicant: false,
    notifyOnReject: false,
    levels: []
  });

  const moduleOptions = [
    'Leave Approval',
    'Advance Receipt Approval',
    'Payroll Processing Approval',
    'Property Contract Approval',
    'Venue Approval',
    'Loan Application Approval',
    'Tour Creation Approval'
  ];

  const typeOptions = [
    'By Designation',
    'Specific Employee',
    'Department Head',
    'Role Based'
  ];

  const employeeOptions = [
    'Sheikh Mohammed Hussein',
    'Husain Saifuddin Khambi',
    'Taher Husain Fakhri',
    'Ahmed Hassan Ali',
    'Fatima Zahra'
  ];

  const departmentOptions = [
    'Any',
    'Finance',
    'HR',
    'Operations',
    'Management'
  ];

  const designationOptions = [
    'Supervisor',
    'Manager',
    'Department Head',
    'Director',
    'Executive'
  ];

  const fallbackApproverOptions = [
    'None',
    'Sheikh Mohammed Hussein',
    'Husain Saifuddin Khambi',
    'Taher Husain Fakhri'
  ];

  const handleAddWorkflow = () => {
    setEditingWorkflow(null);

    setFormErrors({});

    setFormData({
      id: '',
      module: '',
      workflowLabel: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      slaHours: '24',
      escalationAfterHours: '48',
      active: true,
      autoApprove: false,
      notifyApplicant: false,
      notifyOnReject: false,
      levels: []
    });
    setIsDialogOpen(true);
  };

  const handleEditWorkflow = (workflow: WorkflowConfig) => {
    setEditingWorkflow(workflow);
    setFormErrors({});
    setFormData({ ...workflow });
    setIsDialogOpen(true);
  };

  const handleDeleteWorkflow = (workflow: WorkflowConfig) => {
    setDeletingWorkflow(workflow);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteWorkflow = () => {
    if (!deletingWorkflow) return;

    const updatedWorkflows = workflows.filter(
        (w) => w.id !== deletingWorkflow.id
    );

    setWorkflows(updatedWorkflows);

    if (selectedWorkflow?.id === deletingWorkflow.id) {
        setSelectedWorkflow(updatedWorkflows[0] || null);
    }

    toast.success("Workflow deleted successfully");

    setIsDeleteDialogOpen(false);
    setDeletingWorkflow(null);
  };

  const validateWorkflowForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.module) {
        errors.module = "Module is required";
    }

    if (!formData.workflowLabel) {
        errors.workflowLabel = "Workflow Label is required";
    }

    if (!formData.validFrom) {
        errors.validFrom = "Valid From date is required";
    }

    if (!formData.validTo) {
        errors.validTo = "Valid To date is required";
    }

    formData.levels.forEach((level, index) => {
        if (!level.name) {
        errors[`levelName-${level.id}`] = `Level ${index + 1} name is required`;
        }

        if (!level.type) {
        errors[`levelType-${level.id}`] = `Level ${index + 1} type is required`;
        }

        if (level.type === "Specific Employee" && !level.employee) {
        errors[`levelEmployee-${level.id}`] = `Employee is required`;
        }

        if (level.type !== "Specific Employee" && !level.designation) {
        errors[`levelDesignation-${level.id}`] =
            `Designation is required`;
        }
    });

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSaveWorkflow = () => {
    const isValid = validateWorkflowForm();

    if (!isValid) {
      toast.error('Please fill all required fields');
      return;
    }

    if (editingWorkflow) {
      setWorkflows(workflows.map(w =>
        w.id === editingWorkflow.id ? formData : w
      ));
      if (selectedWorkflow?.id === editingWorkflow.id) {
        setSelectedWorkflow(formData);
      }
      toast.success('Workflow updated successfully');
    } else {
      const newWorkflow: WorkflowConfig = {
        ...formData,
        id: Date.now().toString()
      };
      setWorkflows([...workflows, newWorkflow]);
      toast.success('Workflow created successfully');
    }

    setFormErrors({});
    setIsDialogOpen(false);
  };

  const handleAddApprovalLevel = () => {
    const newLevel: ApprovalLevel = {
      id: Date.now().toString(),
      levelNumber: formData.levels.length + 1,
      name: `Approver ${formData.levels.length + 1}`,
      type: '',
      employee: '',
      department: '',
      designation: '',
      fallbackApprover: '',
      canApprove: false,
      canReject: false,
      mandatoryLevel: false
    };
    setFormData({
      ...formData,
      levels: [...formData.levels, newLevel]
    });
  };

  const handleRemoveApprovalLevel = (id: string) => {
    const updatedLevels = formData.levels
      .filter(l => l.id !== id)
      .map((l, idx) => ({ ...l, levelNumber: idx + 1 }));
    setFormData({
      ...formData,
      levels: updatedLevels
    });
  };

  const handleUpdateApprovalLevel = (id: string, updates: Partial<ApprovalLevel>) => {
    setFormData({
      ...formData,
      levels: formData.levels.map(l => l.id === id ? { ...l, ...updates } : l)
    });
  };

  const handleImageUpload = (levelId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleUpdateApprovalLevel(levelId, { image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredWorkflows = workflows.filter(w =>
    w.module.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.workflowLabel.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeWorkflows = workflows.filter(w => w.active).length;
  const totalApprovalLevels = workflows.reduce((sum, w) => sum + w.levels.length, 0);

  return (
    <div className="h-auto lg:h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-4">
      {/* Left Sidebar - Workflow List */}
      <div className="w-full lg:w-72 flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-[#5B9BD5]/10 to-[#5B9BD5]/5">
          <div className="flex items-center justify-end mb-3">
            <Button
              onClick={handleAddWorkflow}
              size="sm"
              className="bg-[#5B9BD5] hover:bg-[#4A8BC2] text-white h-7 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 gap-2 p-3 border-b border-gray-200 bg-gray-50">
          <div className="text-center bg-white rounded p-2 border border-gray-200">
            <div className="text-xl font-bold text-[#5B9BD5]">{activeWorkflows}</div>
            <div className="text-[10px] text-gray-600 mt-0.5 uppercase">Active</div>
          </div>
          <div className="text-center bg-white rounded p-2 border border-gray-200">
            <div className="text-xl font-bold text-[#5B9BD5]">{totalApprovalLevels}</div>
            <div className="text-[10px] text-gray-600 mt-0.5 uppercase">Levels</div>
          </div>
        </div>

        {/* Workflow List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredWorkflows.map((workflow, index) => (
            <button
              key={workflow.id}
              onClick={() => setSelectedWorkflow(workflow)}
              className={cn(
                "w-full text-left p-2.5 rounded transition-colors",
                selectedWorkflow?.id === workflow.id
                  ? "bg-[#5B9BD5] text-white shadow-sm"
                  : "bg-white hover:bg-gray-50 border border-gray-200"
              )}
            >
              <div className="flex items-start gap-2.5">
                <div className={cn(
                  "w-7 h-7 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold",
                  selectedWorkflow?.id === workflow.id
                    ? "bg-[#4A8BC2] text-white"
                    : "bg-gray-100 text-gray-700"
                )}>
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "text-xs font-medium truncate",
                    selectedWorkflow?.id === workflow.id ? "text-white" : "text-gray-900"
                  )}>
                    {workflow.module}
                  </div>
                  <div className={cn(
                    "text-[11px] mt-0.5",
                    selectedWorkflow?.id === workflow.id ? "text-blue-100" : "text-gray-500"
                  )}>
                    {workflow.levels.length} level{workflow.levels.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Content - Workflow Details */}
      <div className="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {selectedWorkflow ? (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#5B9BD5]/10 to-[#5B9BD5]/5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 break-words">
                    {selectedWorkflow.module}
                  </h2>
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span>Valid: {new Date(selectedWorkflow.validFrom).toLocaleDateString()} - {new Date(selectedWorkflow.validTo).toLocaleDateString()}</span>
                    <Badge className={selectedWorkflow.active ? "bg-green-100 text-green-700 text-[10px] h-5" : "bg-gray-100 text-gray-700 text-[10px] h-5"}>
                      {selectedWorkflow.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditWorkflow(selectedWorkflow)}
                    className="h-8 text-xs"
                    style={{ backgroundColor: "white" }}
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 h-8"
                    style={{ backgroundColor: "white" }}
                    onClick={() => handleDeleteWorkflow(selectedWorkflow)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Workflow Settings */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white rounded border border-gray-200 p-2">
                  <div className="text-[10px] text-gray-500 uppercase mb-1">SLA Hours</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedWorkflow.slaHours}</div>
                </div>
                <div className="bg-white rounded border border-gray-200 p-2">
                  <div className="text-[10px] text-gray-500 uppercase mb-1">Escalation After</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedWorkflow.escalationAfterHours} hours</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={selectedWorkflow.active} disabled className="h-3.5 w-3.5" />
                  <span className="text-xs text-gray-700">Active</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={selectedWorkflow.autoApprove} disabled className="h-3.5 w-3.5" />
                  <span className="text-xs text-gray-700">Auto-Approve</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={selectedWorkflow.notifyApplicant} disabled className="h-3.5 w-3.5" />
                  <span className="text-xs text-gray-700">Notify Applicant</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={selectedWorkflow.notifyOnReject} disabled className="h-3.5 w-3.5" />
                  <span className="text-xs text-gray-700">Notify on Reject</span>
                </div>
              </div>
            </div>

            {/* Approval Levels */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-3 uppercase">Approval Levels</h3>
              {selectedWorkflow.levels.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <GitBranch className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">No approval levels configured</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 text-xs h-7"
                    onClick={() => handleEditWorkflow(selectedWorkflow)}
                  >
                    Configure Levels
                  </Button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {selectedWorkflow.levels.map((level) => (
                    <Card key={level.id} className="border-l-4 border-l-[#5B9BD5]">
                      <CardContent style={{ padding: "16px 12px" }}>
                        <div className="flex flex-col sm:flex-row items-start gap-3">
                          <div className="flex-shrink-0">
                            {level.image ? (
                              <img src={level.image} alt={level.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] flex items-center justify-center">
                                <span className="text-white font-bold text-sm">L{level.levelNumber}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1.5">
                              <div>
                                <h4 className="font-semibold text-sm text-gray-900">{level.name}</h4>
                                <p className="text-[11px] text-gray-500">{level.type}</p>
                              </div>
                              <div className="flex gap-1.5">
                                {level.mandatoryLevel && (
                                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] h-5">
                                    Mandatory
                                  </Badge>
                                )}
                                {level.canApprove && (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] h-5">
                                    Can Approve
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                              {level.type === 'Specific Employee' ? (
                                <div>
                                  <span className="text-gray-500">Employee:</span>
                                  <p className="font-medium text-gray-900">{level.employee || 'Not set'}</p>
                                </div>
                              ) : (
                                <>
                                  <div>
                                    <span className="text-gray-500">Department:</span>
                                    <p className="font-medium text-gray-900">{level.department || 'Not set'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Designation:</span>
                                    <p className="font-medium text-gray-900">{level.designation || 'Not set'}</p>
                                  </div>
                                </>
                              )}
                              <div>
                                <span className="text-gray-500">Fallback:</span>
                                <p className="font-medium text-gray-900">{level.fallbackApprover || 'None'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Select a workflow to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Workflow Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[95vw] sm:!w-[80vw] lg:!w-[calc(80vw-1rem)] xl:!w-[60vw] !max-w-[1200px] max-h-[95vh] overflow-y-auto p-4">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {editingWorkflow ? 'Edit Workflow' : 'New Workflow'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* General Settings */}
            <div className="space-y-3 bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900">General Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <SelectFieldset label="Module" required>
                    <Select
                        value={formData.module}
                        onValueChange={(val) => setFormData({ ...formData, module: val })}
                    >
                        <SelectTriggerUI>
                        <SelectValue placeholder="Select module" />
                        </SelectTriggerUI>
                        <SelectContent>
                        {moduleOptions.map(option => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {formErrors.module && (
                    <p className="text-red-500 text-xs mt-1">
                        {formErrors.module}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Workflow Label"
                    required
                    value={formData.workflowLabel}
                    onChange={(e) => {
                      setFormData({ ...formData, workflowLabel: e.target.value });
                      setFormErrors({ ...formErrors, workflowLabel: "" });
                    }}
                    placeholder="e.g. Leave Approval Workflow"
                  />
                  {formErrors.workflowLabel && (
                    <p className="text-red-500 text-xs mt-1">
                        {formErrors.workflowLabel}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Valid From"
                    required
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => {
                      setFormData({ ...formData, validFrom: e.target.value });
                      setFormErrors({ ...formErrors, validFrom: "" });
                    }}
                  />
                  {formErrors.validFrom && (
                    <p className="text-red-500 text-xs mt-1">
                        {formErrors.validFrom}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Valid To"
                    required
                    type="date"
                    value={formData.validTo}
                    onChange={(e) => {
                      setFormData({ ...formData, validTo: e.target.value });
                      setFormErrors({ ...formErrors, validTo: "" });
                    }}
                  />
                  {formErrors.validTo && (
                    <p className="text-red-500 text-xs mt-1">
                        {formErrors.validTo}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <InputFieldset
                    label="SLA (Hours)"
                    type="number"
                    value={formData.slaHours}
                    onChange={(e) => setFormData({ ...formData, slaHours: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Escalation After (Hours)"
                    type="number"
                    value={formData.escalationAfterHours}
                    onChange={(e) => setFormData({ ...formData, escalationAfterHours: e.target.value })}
                  />
                </div>
              </div>

              {/* Workflow Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: !!checked })}
                    className="h-4 w-4"
                  />
                  <Label className="text-xs font-normal cursor-pointer">Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.autoApprove}
                    onCheckedChange={(checked) => setFormData({ ...formData, autoApprove: !!checked })}
                    className="h-4 w-4"
                  />
                  <Label className="text-xs font-normal cursor-pointer">Auto-Approve</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.notifyApplicant}
                    onCheckedChange={(checked) => setFormData({ ...formData, notifyApplicant: !!checked })}
                    className="h-4 w-4"
                  />
                  <Label className="text-xs font-normal cursor-pointer">Notify Applicant</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.notifyOnReject}
                    onCheckedChange={(checked) => setFormData({ ...formData, notifyOnReject: !!checked })}
                    className="h-4 w-4"
                  />
                  <Label className="text-xs font-normal cursor-pointer">Notify on Reject</Label>
                </div>
              </div>
            </div>

            {/* Approval Levels */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Approval Levels</h3>
              {formData.levels.map((level) => (
                <Card key={level.id} className="p-3 bg-gray-50 border-gray-200">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="w-9 h-9 rounded-lg bg-[#2B3F54] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                        L{level.levelNumber}
                      </div>
                      <Input
                        value={level.name}
                        onChange={(e) => {
                          handleUpdateApprovalLevel(level.id, { name: e.target.value });

                          setFormErrors({
                            ...formErrors,
                            [`levelName-${level.id}`]: "",
                          });
                        }}
                        placeholder="e.g., Direct Supervisor"
                        className="font-medium h-9 flex-1 min-w-0"
                      />
                      {formErrors[`levelName-${level.id}`] && (
                        <p className="text-red-500 text-xs mt-1">
                            {formErrors[`levelName-${level.id}`]}
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveApprovalLevel(level.id)}
                        className="text-red-600 hover:bg-red-50 h-9 w-9 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <SelectFieldset label="Type" required>
                          <Select
                            value={level.type}
                            onValueChange={(val) => handleUpdateApprovalLevel(level.id, { type: val })}
                            >
                            <SelectTriggerUI className="text-xs">
                                <SelectValue placeholder="Select type" />
                            </SelectTriggerUI>
                            <SelectContent>
                                {typeOptions.map(option => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                        {formErrors[`levelType-${level.id}`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {formErrors[`levelType-${level.id}`]}
                          </p>
                        )}
                      </div>
                      {level.type === 'Specific Employee' ? (
                        <div className="space-y-1">
                          <SelectFieldset label="Employee" required>
                            <Select
                              value={level.employee}
                              onValueChange={(val) => handleUpdateApprovalLevel(level.id, { employee: val })}
                            >
                              <SelectTriggerUI className="text-xs">
                                <SelectValue placeholder="Select employee..." />
                              </SelectTriggerUI>
                              <SelectContent>
                                {employeeOptions.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                          {formErrors[`levelEmployee-${level.id}`] && (
                            <p className="text-red-500 text-xs mt-1">
                                {formErrors[`levelEmployee-${level.id}`]}
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-1">
                            <SelectFieldset label="Department">
                              <Select
                                value={level.department}
                                onValueChange={(val) => handleUpdateApprovalLevel(level.id, { department: val })}
                              >
                                <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select" />
                                </SelectTriggerUI>
                                <SelectContent>
                                    {departmentOptions.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </SelectFieldset>
                          </div>
                          <div className="space-y-1">
                            <SelectFieldset label="Designation" required>
                              <Select
                                value={level.designation}
                                onValueChange={(val) => handleUpdateApprovalLevel(level.id, { designation: val })}
                              >
                                <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select" />
                                </SelectTriggerUI>
                                <SelectContent>
                                    {designationOptions.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </SelectFieldset>
                            {formErrors[`levelDesignation-${level.id}`] && (
                              <p className="text-red-500 text-xs mt-1">
                                {formErrors[`levelDesignation-${level.id}`]}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                      <div className="space-y-1">
                        <SelectFieldset label="Fallback Approver">
                          <Select
                            value={level.fallbackApprover}
                            onValueChange={(val) => handleUpdateApprovalLevel(level.id, { fallbackApprover: val })}
                          >
                            <SelectTriggerUI className="text-xs">
                                <SelectValue placeholder="None" />
                            </SelectTriggerUI>
                            <SelectContent>
                                {fallbackApproverOptions.map(option => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </SelectFieldset>
                      </div>
                    </div>

                   <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                        <div className="flex items-center gap-1.5">
                          <Checkbox
                            checked={level.canApprove}
                            onCheckedChange={(checked) => handleUpdateApprovalLevel(level.id, { canApprove: !!checked })}
                            className="h-4 w-4"
                          />
                          <Label className="text-xs font-normal cursor-pointer">Can Approve</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Checkbox
                            checked={level.canReject}
                            onCheckedChange={(checked) => handleUpdateApprovalLevel(level.id, { canReject: !!checked })}
                            className="h-4 w-4"
                          />
                          <Label className="text-xs font-normal cursor-pointer">Can Reject</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Checkbox
                            checked={level.mandatoryLevel}
                            onCheckedChange={(checked) => handleUpdateApprovalLevel(level.id, { mandatoryLevel: !!checked })}
                            className="h-4 w-4"
                          />
                          <Label className="text-xs font-normal cursor-pointer">Mandatory Level</Label>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 items-center">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(level.id, e)}
                          />
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 rounded hover:bg-gray-50 text-xs">
                            <Upload className="w-3 h-3" />
                            {level.image ? 'Change' : 'Upload'}
                          </div>
                        </label>
                        {level.image && (
                          <img src={level.image} alt="Preview" className="w-7 h-7 rounded object-cover" />
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={handleAddApprovalLevel}
                className="w-full border-dashed h-9 text-xs text-[#5B9BD5] hover:bg-[#5B9BD5]/5"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Approval Level
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="h-9 text-xs">
              Cancel
            </Button>
            <Button onClick={handleSaveWorkflow} className="bg-[#2B3F54] hover:bg-[#1F2D3D] text-white h-9 text-xs">
              Save Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete Workflow
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{deletingWorkflow?.module}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingWorkflow(null);
              }}
            >
              No, Cancel
            </Button>
            <Button
              onClick={confirmDeleteWorkflow}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
